// src/state/editFeaturesStore.js
// Stores extracted features + embedding per pin, per editId.
//
// Keyed by editId -> { [pinId]: { kind, features, embedding, updatedAt, sourceHash } }
//
// We only compute for:
//  - kind === "note"
//  - kind === "image"
//
// We avoid pinned_product because catalog already has metadata.

const KEY = (editId) => `veya_edit_features_v1::${editId}`;

function safeJsonParse(raw, fallback) {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function safeRead(editId) {
  if (!editId) return {};
  const raw = localStorage.getItem(KEY(editId));
  const obj = safeJsonParse(raw, {});
  return obj && typeof obj === "object" ? obj : {};
}

function safeWrite(editId, obj) {
  if (!editId) return;
  try {
    localStorage.setItem(KEY(editId), JSON.stringify(obj || {}));
  } catch {}
}

// tiny stable hash to detect changes without storing whole blobs twice
function hashString(str) {
  const s = String(str || "");
  let h = 2166136261; // FNV-1a base
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // unsigned
  return (h >>> 0).toString(16);
}

export function isFeatureEligiblePin(pin) {
  const kind = String(pin?.kind || "").toLowerCase();
  return kind === "note" || kind === "image";
}

export function getEditFeaturesMap(editId) {
  // returns { [pinId]: entry }
  return safeRead(editId);
}

export function getPinFeatures(editId, pinId) {
  if (!editId || !pinId) return null;
  const map = safeRead(editId);
  return map[pinId] || null;
}

export function setPinFeatures(editId, pinId, entry) {
  if (!editId || !pinId) return;
  const map = safeRead(editId);

  map[pinId] = {
    ...map[pinId],
    ...entry,
    updatedAt: Date.now(),
  };

  safeWrite(editId, map);
  return map[pinId];
}

export function deletePinFeatures(editId, pinId) {
  if (!editId || !pinId) return;
  const map = safeRead(editId);
  if (map[pinId]) {
    delete map[pinId];
    safeWrite(editId, map);
  }
}

export function clearEditFeatures(editId) {
  if (!editId) return;
  safeWrite(editId, {});
}

/**
 * Computes a "sourceHash" for a pin so we can skip recompute if unchanged.
 * - note: hash(text)
 * - image: hash(imageUrl) (works because you store dataUrls; if later you store remote urls, still ok)
 */
export function computePinSourceHash(pin) {
  const kind = String(pin?.kind || "").toLowerCase();
  if (kind === "note") return hashString(pin?.text || pin?.note || "");
  if (kind === "image") return hashString(pin?.imageUrl || pin?.url || "");
  return null;
}